import { useMemo, useState } from 'react'

/**
 * Ordinamento e filtro sulle colonne di una tabella.
 *
 * <p>Le tabelle di KPO hanno celle molto diverse fra loro — campi di quantità, pastiglie di stato,
 * numeri allineati a destra — quindi qui non si genera il corpo della tabella: si generano solo le
 * <b>intestazioni</b>, e la pagina continua a disegnare le righe come vuole. Il prezzo è che ogni
 * tabella dichiari come si legge una colonna; il vantaggio è che nessuna cella perde il suo
 * comportamento.</p>
 */
export interface ColumnDef<T> {
  key: string
  label: string

  /** Come si legge questa colonna per ordinare e filtrare. */
  value: (row: T) => string | number | null | undefined

  /** Allinea a destra e ordina come numero. */
  numeric?: boolean

  /** Colonna di sola presentazione: niente ordinamento né filtro (per esempio i comandi). */
  plain?: boolean

  title?: string
}

export type SortDirection = 'asc' | 'desc'

export interface Grid<T> {
  rows: T[]
  sortKey?: string
  sortDirection: SortDirection
  toggleSort: (key: string) => void
  filters: Record<string, string>
  setFilter: (key: string, value: string) => void
  clearFilters: () => void
  activeFilters: number
  /** Righe scartate dai filtri: serve a dirlo, invece di lasciar credere che non ce ne siano. */
  hiddenCount: number
}

/**
 * Filtra e ordina in memoria. Le tabelle in questione arrivano a qualche centinaio di righe: sono
 * già tutte nel browser, e farlo qui evita un giro sul server a ogni tasto.
 */
export function useGrid<T>(rows: T[], columns: ColumnDef<T>[]): Grid<T> {
  const [sortKey, setSortKey] = useState<string>()
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [filters, setFilters] = useState<Record<string, string>>({})

  const byKey = useMemo(
    () => new Map(columns.map((c) => [c.key, c])),
    [columns],
  )

  const filtered = useMemo(() => {
    const active = Object.entries(filters).filter(([, v]) => v.trim() !== '')
    if (active.length === 0) return rows

    return rows.filter((row) =>
      active.every(([key, text]) => {
        const column = byKey.get(key)
        return column ? matches(column.value(row), text, column.numeric) : true
      }),
    )
  }, [rows, filters, byKey])

  const sorted = useMemo(() => {
    const column = sortKey ? byKey.get(sortKey) : undefined
    if (!column) return filtered

    const sign = sortDirection === 'asc' ? 1 : -1

    // Copia: l'ordinamento non deve riordinare l'array del chiamante.
    return [...filtered].sort((a, b) => sign * compare(column.value(a), column.value(b), column.numeric))
  }, [filtered, sortKey, sortDirection, byKey])

  return {
    rows: sorted,
    sortKey,
    sortDirection,
    // Tre stati e non due: dopo crescente e decrescente si torna all'ordine naturale, che per una
    // proposta è quello in cui il motore ha elencato gli articoli.
    toggleSort: (key) =>
      setSortKey((current) => {
        if (current !== key) {
          setSortDirection('asc')
          return key
        }
        if (sortDirection === 'asc') {
          setSortDirection('desc')
          return key
        }
        return undefined
      }),
    filters,
    setFilter: (key, value) => setFilters((f) => ({ ...f, [key]: value })),
    clearFilters: () => setFilters({}),
    activeFilters: Object.values(filters).filter((v) => v.trim() !== '').length,
    hiddenCount: rows.length - sorted.length,
  }
}

/**
 * I valori assenti vanno in fondo in entrambi i versi: sono «non pervenuto», non «zero», e
 * portarli in cima invertendo l'ordine non aiuterebbe nessuno.
 */
function compare(a: unknown, b: unknown, numeric?: boolean): number {
  const aEmpty = a === null || a === undefined || a === ''
  const bEmpty = b === null || b === undefined || b === ''
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1

  if (numeric) return Number(a) - Number(b)
  return String(a).localeCompare(String(b), 'it', { numeric: true, sensitivity: 'base' })
}

/**
 * Sulle colonne di testo il filtro cerca una porzione. Su quelle numeriche accetta anche un
 * confronto — `>10`, `<=3`, `=0` — perché su una colonna di quantità la domanda vera non è «quali
 * contengono un 4» ma «quali sono sopra una certa soglia».
 */
function matches(value: unknown, filter: string, numeric?: boolean): boolean {
  const text = filter.trim()
  if (text === '') return true

  if (numeric) {
    const operator = /^(>=|<=|<>|>|<|=)\s*(-?\d+(?:[.,]\d+)?)$/.exec(text)
    if (operator) {
      const threshold = Number(operator[2].replace(',', '.'))
      const current = value === null || value === undefined || value === '' ? null : Number(value)
      if (current === null || Number.isNaN(current) || Number.isNaN(threshold)) return false

      switch (operator[1]) {
        case '>': return current > threshold
        case '<': return current < threshold
        case '>=': return current >= threshold
        case '<=': return current <= threshold
        case '<>': return current !== threshold
        default: return current === threshold
      }
    }
  }

  return String(value ?? '').toLowerCase().includes(text.toLowerCase())
}

/**
 * Intestazione della tabella: ogni colonna è un pulsante che ordina e, sotto, una casella che
 * filtra. Il filtro sta nell'intestazione e non in una barra separata perché così si vede a colpo
 * d'occhio quale colonna è ristretta.
 */
export function GridHead<T>({
  columns,
  grid,
  extraColumns = 0,
}: {
  columns: ColumnDef<T>[]
  grid: Grid<T>
  /** Colonne in coda senza intestazione, per esempio quella dei comandi. */
  extraColumns?: number
}) {
  return (
    <thead>
      <tr>
        {columns.map((c) => {
          const sorted = grid.sortKey === c.key
          return (
            <th
              key={c.key}
              className={c.numeric ? 'num' : undefined}
              title={c.title}
              aria-sort={sorted ? (grid.sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              {c.plain ?
                c.label
              : <button
                  type="button"
                  className={`grid-sort${sorted ? ' is-sorted' : ''}`}
                  onClick={() => grid.toggleSort(c.key)}
                  title={
                    sorted && grid.sortDirection === 'asc' ? 'Ordina al contrario'
                    : sorted ? 'Togli l’ordinamento'
                    : `Ordina per ${c.label.toLowerCase()}`
                  }
                >
                  {c.label}
                  <span className="grid-sort-mark" aria-hidden>
                    {sorted ? (grid.sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                  </span>
                </button>
              }
            </th>
          )
        })}
        {Array.from({ length: extraColumns }, (_, i) => <th key={`extra-${i}`} />)}
      </tr>

      <tr className="grid-filters">
        {columns.map((c) => (
          <th key={c.key}>
            {!c.plain && (
              <input
                value={grid.filters[c.key] ?? ''}
                onChange={(e) => grid.setFilter(c.key, e.target.value)}
                placeholder={c.numeric ? '>0' : 'filtra'}
                aria-label={`Filtra per ${c.label.toLowerCase()}`}
                className={grid.filters[c.key]?.trim() ? 'is-active' : undefined}
              />
            )}
          </th>
        ))}
        {Array.from({ length: extraColumns }, (_, i) => <th key={`extra-${i}`} />)}
      </tr>
    </thead>
  )
}

/** Riga di riepilogo sotto la tabella: quante righe si stanno vedendo e come toglierne il filtro. */
export function GridSummary<T>({ grid, total }: { grid: Grid<T>; total: number }) {
  if (grid.activeFilters === 0) return null

  return (
    <div className="grid-summary muted">
      {grid.rows.length} righe su {total}
      {grid.hiddenCount > 0 && ` · ${grid.hiddenCount} nascoste dal filtro`}
      <button type="button" className="grid-clear" onClick={grid.clearFilters}>
        Togli i filtri
      </button>
    </div>
  )
}
